import { Link2, Link2Off, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCompanies } from "@/hooks/use-companies";
import {
  type Contact,
  useAuthUser,
  useContacts,
  useCreateContact,
  useDeleteContact,
  useSearchAuthUsers,
  useUpdateContact,
} from "@/hooks/use-contacts";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  companyId: string;
  userId: string | null;
}

const EMPTY_FORM: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  title: "",
  companyId: "",
  userId: null,
};

export function ContactsPage() {
  const { data, isLoading, isError, error, refetch } = useContacts();
  const { data: companies } = useCompanies();
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const focusedRowRef = useRef<HTMLTableRowElement | null>(null);

  function openCreateDialog() {
    setEditingContact(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEditDialog(contact: Contact) {
    setEditingContact(contact);
    setFormData({
      firstName: contact.firstName ?? "",
      lastName: contact.lastName ?? "",
      email: contact.email ?? "",
      phone: contact.phone ?? "",
      title: contact.title ?? "",
      companyId: contact.companyId ?? "",
      userId: contact.userId ?? null,
    });
    setDialogOpen(true);
  }

  function handleSubmit() {
    const payload = { ...formData };
    if (editingContact) {
      updateContact.mutate(
        { id: editingContact.id, ...payload },
        { onSuccess: () => setDialogOpen(false) },
      );
    } else {
      createContact.mutate(payload, { onSuccess: () => setDialogOpen(false) });
    }
  }

  function handleDelete() {
    if (!deleteTarget) return;
    deleteContact.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  // ?focus=<id> deep-link: scroll the row into view, highlight it briefly, and open
  // the edit dialog. Used by the project Team Contacts tab "View in directory" action.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("focus");
    if (id) setFocusedId(id);
  }, []);

  useEffect(() => {
    if (!focusedId || !data) return;
    const target = data.find((c) => c.id === focusedId);
    if (!target) return;
    // Defer until rows render. Inlined (instead of calling openEditDialog) to keep
    // effect deps stable — openEditDialog is recreated on every render.
    const timer = window.setTimeout(() => {
      focusedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setEditingContact(target);
      setFormData({
        firstName: target.firstName ?? "",
        lastName: target.lastName ?? "",
        email: target.email ?? "",
        phone: target.phone ?? "",
        title: target.title ?? "",
        companyId: target.companyId ?? "",
        userId: target.userId ?? null,
      });
      setDialogOpen(true);
      const clearTimer = window.setTimeout(() => setFocusedId(null), 2000);
      return () => window.clearTimeout(clearTimer);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [focusedId, data]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error?.message ?? "Failed to load contacts"}</p>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  const items = (data ?? []).filter((contact) => {
    const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(" ").toLowerCase();
    const matchesSearch =
      search.trim() === "" ||
      fullName.includes(search.toLowerCase()) ||
      (contact.email ?? "").toLowerCase().includes(search.toLowerCase());
    const matchesCompany = companyFilter === "all" || contact.companyId === companyFilter;
    return matchesSearch && matchesCompany;
  });

  const isMutating = createContact.isPending || updateContact.isPending;
  const companyList = companies ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Contacts</h1>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add Contact
        </Button>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by company" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companyList.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.companyName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No contacts found.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Trade</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((contact) => {
                const company = companyList.find((c) => c.id === contact.companyId);
                const isFocused = focusedId === contact.id;
                return (
                  <TableRow
                    key={contact.id}
                    ref={isFocused ? focusedRowRef : undefined}
                    className={`hover:bg-muted/50 ${isFocused ? "ring-2 ring-[#415A77] ring-offset-1 transition-shadow" : ""}`}
                  >
                    <TableCell className="font-medium">
                      {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "--"}
                    </TableCell>
                    <TableCell>{contact.title ?? "--"}</TableCell>
                    <TableCell>{contact.email ?? "--"}</TableCell>
                    <TableCell>{contact.phone ?? "--"}</TableCell>
                    <TableCell>{company?.companyName ?? "--"}</TableCell>
                    <TableCell>{company?.tradeCategoryName ?? "--"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(contact)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(contact)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContact ? "Edit Contact" : "Add Contact"}</DialogTitle>
            <DialogDescription>
              {editingContact
                ? "Update the contact details below."
                : "Fill in the details to create a new contact."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="companyId">
                Company <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.companyId}
                onValueChange={(v) => setFormData((p) => ({ ...p, companyId: v }))}
              >
                <SelectTrigger id="companyId">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companyList.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editingContact ? (
              <LinkedUserSection
                userId={formData.userId}
                contactEmail={formData.email}
                onLink={(id) => setFormData((p) => ({ ...p, userId: id }))}
                onUnlink={() => setFormData((p) => ({ ...p, userId: null }))}
              />
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isMutating ||
                !formData.firstName.trim() ||
                !formData.lastName.trim() ||
                !formData.companyId
              }
            >
              {isMutating ? "Saving..." : editingContact ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              {[deleteTarget?.firstName, deleteTarget?.lastName].filter(Boolean).join(" ")}? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteContact.isPending}>
              {deleteContact.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface LinkedUserSectionProps {
  userId: string | null;
  contactEmail: string;
  onLink: (userId: string) => void;
  onUnlink: () => void;
}

function LinkedUserSection({ userId, contactEmail, onLink, onUnlink }: LinkedUserSectionProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");
  const { data: linkedUser, isLoading } = useAuthUser(userId);
  const { data: results } = useSearchAuthUsers(showPicker ? search || contactEmail : "");

  return (
    <div className="grid gap-2 rounded-md border bg-muted/30 p-3">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Linked user</Label>
      {userId ? (
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm">
            {isLoading ? (
              <span className="text-muted-foreground">Loading…</span>
            ) : linkedUser ? (
              <div className="flex flex-col">
                <span className="font-medium">{linkedUser.name}</span>
                <span className="text-xs text-muted-foreground">
                  {linkedUser.email} ·{" "}
                  <Badge variant={linkedUser.status === "active" ? "success" : "destructive"}>
                    {linkedUser.status}
                  </Badge>
                </span>
              </div>
            ) : (
              <span className="text-destructive text-xs">User not found ({userId})</span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={onUnlink}>
            <Link2Off className="mr-1 h-3 w-3" />
            Unlink
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">No linked user</span>
          <Button variant="outline" size="sm" onClick={() => setShowPicker((v) => !v)}>
            <Link2 className="mr-1 h-3 w-3" />
            {showPicker ? "Cancel" : "Link user"}
          </Button>
        </div>
      )}
      {showPicker && !userId ? (
        <div className="space-y-2">
          <Input
            placeholder="Search users by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="max-h-40 overflow-auto rounded border bg-background">
            {(results ?? []).length === 0 ? (
              <div className="p-2 text-xs text-muted-foreground">No matching users.</div>
            ) : (
              (results ?? []).map((u) => (
                <button
                  type="button"
                  key={u.id}
                  className="flex w-full items-center justify-between gap-2 border-b px-2 py-1.5 text-left text-sm last:border-0 hover:bg-muted"
                  onClick={() => {
                    onLink(u.id);
                    setShowPicker(false);
                    setSearch("");
                  }}
                >
                  <span className="truncate">
                    {u.name} <span className="text-muted-foreground">· {u.email}</span>
                  </span>
                  <Badge variant={u.status === "active" ? "success" : "destructive"}>
                    {u.status}
                  </Badge>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
